import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import SearchableSelect from './SearchableSelect';
import Loader from './Loader';
import QRCodeGenerator from './QRCodeGenerator';
import AssignmentBulkOperations from './AssignmentBulkOperations';
import AssignmentEditorModal from './AssignmentEditorModal';
import AssignmentBatchEditModal from './AssignmentBatchEditModal';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import { formatDateForInputIST, formatDisplayDateIST, formatDisplayDateTimeIST, getTodayInputDateIST } from '../utils/dateUtils';
import ReceiptService from '../services/ReceiptService';
import { fetchAllPaginatedItems } from '../utils/paginationUtils';

const API_URL = '/api';

function AssetAssignments() {
  const emptyFilters = {
    assetType: '',
    employee: '',
    deliveryStatus: '',
    status: '',
    dateRange: { start: '', end: '' }
  };

  const getInitialFormData = () => ({
    employeeId: '',
    selectedAssets: [],
    assignedDate: getTodayInputDateIST(),
    deliveryMethod: 0,
    deliveryStatus: 0,
    courierService: '',
    trackingId: '',
    pickupPersonName: '',
    pickupPersonMobile: '',
    notes: ''
  });

  const getInitialBatchEditData = () => ({
    selectedAssignments: [],
    selectedProperties: [],
    propertyToAdd: '',
    assignedDate: getTodayInputDateIST(),
    deliveryMethod: 0,
    deliveryStatus: 0,
    courierService: '',
    trackingId: '',
    pickupPersonName: '',
    pickupPersonMobile: '',
    notes: ''
  });

  const [assignments, setAssignments] = useState([]);
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize] = useState(25);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [totalEmployeeGroups, setTotalEmployeeGroups] = useState(0);
  const [assignmentSummary, setAssignmentSummary] = useState({
    total: 0,
    active: 0,
    returned: 0,
    delivered: 0,
    inTransit: 0,
    pending: 0,
    transfers: 0
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState(emptyFilters);
  const [assetFilter, setAssetFilter] = useState('');
  const [formData, setFormData] = useState(getInitialFormData);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [batchEditData, setBatchEditData] = useState(getInitialBatchEditData);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    fromEmployeeId: '',
    assignmentId: '',
    toEmployeeId: '',
    notes: ''
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineAssetId, setTimelineAssetId] = useState(null);
  const [assetTimeline, setAssetTimeline] = useState([]);
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [groupByEmployee, setGroupByEmployee] = useState(false);
  const [groupedCurrentPage, setGroupedCurrentPage] = useState(1);
  const [groupedItemsPerPage] = useState(10);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  const [showReturnDeadModal, setShowReturnDeadModal] = useState(false);
  const [selectedAssignmentForDeadReturn, setSelectedAssignmentForDeadReturn] = useState(null);
  const [deadReturnNotes, setDeadReturnNotes] = useState('');
  const { alert, showAlert, hideAlert } = useAlert();


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAvailableAssets(),
        fetchEmployees()
      ]);
    };
    loadData();
    document.title = 'Asset Assignments - Saral Asset Manager';
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setTablePage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (groupByEmployee) {
      fetchAssignmentGroups();
    } else {
      fetchAssignments();
    }
  }, [groupByEmployee, tablePage, tablePageSize, groupedCurrentPage, debouncedSearchTerm, filters, sortField, sortDirection]);

  const getAssignmentQueryParams = (page = tablePage, pageSize = tablePageSize, includeSearch = true) => ({
    page,
    pageSize,
    search: includeSearch && debouncedSearchTerm ? debouncedSearchTerm : undefined,
    assetType: filters.assetType || undefined,
    employeeId: filters.employee || undefined,
    deliveryStatus: filters.deliveryStatus !== '' ? filters.deliveryStatus : undefined,
    status: filters.status || undefined,
    dateFrom: filters.dateRange.start || undefined,
    dateTo: filters.dateRange.end || undefined,
    sortBy: sortField || undefined,
    sortDir: sortField ? sortDirection : undefined
  });

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/assetassignments/paged`, {
        params: getAssignmentQueryParams()
      });

      setAssignments(response.data?.items || []);
      setTotalAssignments(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignments([]);
      setTotalAssignments(0);
    } finally {
      fetchAssignmentSummary();
      setLoading(false);
    }
  };

  const fetchAssignmentGroups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/assetassignments/grouped-by-employee`, {
        params: {
          page: groupedCurrentPage,
          pageSize: groupedItemsPerPage,
          search: debouncedSearchTerm || undefined,
          assetType: filters.assetType || undefined,
          employeeId: filters.employee || undefined,
          deliveryStatus: filters.deliveryStatus !== '' ? filters.deliveryStatus : undefined,
          status: filters.status || undefined,
          dateFrom: filters.dateRange.start || undefined,
          dateTo: filters.dateRange.end || undefined
        }
      });

      setAssignmentGroups(response.data?.items || []);
      setTotalEmployeeGroups(response.data?.total || 0);
      const validIds = new Set((response.data?.items || []).map(group => group.employeeId));
      setExpandedEmployees(prev => new Set([...prev].filter(id => validIds.has(id))));
    } catch (error) {
      console.error('Error fetching assignment groups:', error);
      setAssignmentGroups([]);
      setTotalEmployeeGroups(0);
    } finally {
      fetchAssignmentSummary();
      setLoading(false);
    }
  };

  const fetchAssignmentSummary = async () => {
    setSummaryLoading(true);
    try {
      const allAssignments = await fetchAllPaginatedItems(async (page, pageSize) => {
        const response = await axios.get(`${API_URL}/assetassignments/paged`, {
          params: { page, pageSize }
        });

        return response.data;
      });

      const employeeIds = new Set();
      let active = 0;
      let returned = 0;
      let delivered = 0;
      let inTransit = 0;
      let pending = 0;
      let transfers = 0;

      allAssignments.forEach((assignment) => {
        const employeeId = assignment.employeeId ?? assignment.EmployeeId;
        const isActive = Boolean(assignment.isActive ?? assignment.IsActive);
        const deliveryStatus = Number(assignment.deliveryStatus ?? assignment.DeliveryStatus ?? 0);
        const deliveryMethod = Number(assignment.deliveryMethod ?? assignment.DeliveryMethod ?? 0);

        if (employeeId) {
          employeeIds.add(String(employeeId));
        }

        if (isActive) {
          active += 1;
        } else {
          returned += 1;
        }

        if (deliveryStatus === 2) delivered += 1;
        if (deliveryStatus === 1) inTransit += 1;
        if (deliveryStatus === 0) pending += 1;
        if (deliveryMethod === 3) transfers += 1;
      });

      setAssignmentSummary({
        total: allAssignments.length,
        active,
        returned,
        delivered,
        inTransit,
        pending,
        transfers
      });
    } catch (error) {
      console.error('Error fetching assignment summary:', error);
      setAssignmentSummary({
        total: 0,
        active: 0,
        returned: 0,
        delivered: 0,
        inTransit: 0,
        pending: 0,
        transfers: 0
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchAvailableAssets = async () => {
    try {
      const response = await axios.get(`${API_URL}/assets`);
      const availableAssets = response.data.filter(asset => {
        const assigned = asset.assignedCount || 0;
        const dead = asset.deadCount || 0;
        const remaining = asset.quantity - assigned - dead;
        return remaining > 0 && asset.status !== 'Dead';
      });
      setAssets(availableAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      console.log('Employees data:', response.data); // Debug log
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const printAssignmentReceiptForRow = async (assignment) => {
    const assignmentId = assignment?.id ?? assignment?.Id;
    const isTransfer = (assignment?.deliveryMethod ?? assignment?.DeliveryMethod) === 3;
    if (!assignmentId) return;

    if (isTransfer) {
      await ReceiptService.generateTransferReceipt(assignmentId);
      return;
    }

    await ReceiptService.generateAssignmentReceipt([assignmentId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      console.log('Submitting assignments:', formData); // Debug log

      if (editingAssignment) {
        const payload = {
          assignedDate: formData.assignedDate || null,
          serialNumber: formData.selectedAssets[0]?.serialNumber?.trim() || null,
          deliveryMethod: formData.deliveryMethod,
          deliveryStatus: formData.deliveryStatus,
          courierService: formData.courierService || null,
          trackingId: formData.trackingId || null,
          pickupPersonName: formData.pickupPersonName || null,
          pickupPersonMobile: formData.pickupPersonMobile || null,
          notes: formData.notes || null
        };
        await axios.put(`${API_URL}/assetassignments/${editingAssignment.id}`, payload);
      } else {
        const createdAssignments = await Promise.all(
          formData.selectedAssets.map(assetData => {
            const payload = {
              assetId: parseInt(assetData.assetId),
              employeeId: formData.employeeId,
              serialNumber: assetData.serialNumber,
              assignedDate: formData.assignedDate || null,
              deliveryMethod: formData.deliveryMethod,
              courierService: formData.courierService,
              trackingId: formData.trackingId,
              pickupPersonName: formData.pickupPersonName,
              pickupPersonMobile: formData.pickupPersonMobile,
              notes: formData.notes
            };
            console.log('Assignment payload:', payload); // Debug log
            return axios.post(`${API_URL}/assetassignments`, payload);
          })
        );

      }

      handleCloseModal();
      showAlert('success', editingAssignment ? 'Assignment updated successfully!' : `${formData.selectedAssets.length} asset(s) assigned successfully!`);
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error creating assignment:', error);
      console.error('Error response:', error.response?.data); // Debug error details
      const errorMsg = error.response?.data || error.message || 'Failed to assign assets';
      showAlert('danger', `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setEditingAssignment(null);
    setFormData(getInitialFormData());
    setShowModal(true);
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setBatchEditData({
      selectedAssignments: [{
        id: assignment.id,
        assetId: assignment.assetId,
        assetName: assignment.asset?.name || assignment.asset?.Name || 'Unknown Asset',
        assetType: getAssetTypeName(assignment),
        employeeName: assignment.employeeName || assignment.EmployeeName || assignment.employeeId || assignment.EmployeeId || 'Unknown',
        serialNumber: assignment.serialNumber || '',
        isActive: assignment.isActive,
        assignedDate: assignment.assignedDate || assignment.AssignedDate || null,
        deliveryMethod: assignment.deliveryMethod ?? assignment.DeliveryMethod ?? 0,
        deliveryStatus: assignment.deliveryStatus ?? assignment.DeliveryStatus ?? 0,
        courierService: assignment.courierService || assignment.CourierService || '',
        trackingId: assignment.trackingId || assignment.TrackingId || '',
        pickupPersonName: assignment.pickupPersonName || assignment.PickupPersonName || '',
        pickupPersonMobile: assignment.pickupPersonMobile || assignment.PickupPersonMobile || '',
        notes: assignment.notes || assignment.Notes || ''
      }],
      selectedProperties: [],
      propertyToAdd: '',
      assignedDate: formatDateForInputIST(assignment.assignedDate || assignment.AssignedDate) || getTodayInputDateIST(),
      deliveryMethod: assignment.deliveryMethod ?? assignment.DeliveryMethod ?? 0,
      deliveryStatus: assignment.deliveryStatus ?? assignment.DeliveryStatus ?? 0,
      courierService: assignment.courierService || assignment.CourierService || '',
      trackingId: assignment.trackingId || assignment.TrackingId || '',
      pickupPersonName: assignment.pickupPersonName || assignment.PickupPersonName || '',
      pickupPersonMobile: assignment.pickupPersonMobile || assignment.PickupPersonMobile || '',
      notes: assignment.notes || assignment.Notes || ''
    });
    setShowBatchEditModal(true);
  };

  const handleEditSelected = async () => {
    if (selectedIds.length === 0) return;

    setSaving(true);
    try {
      const selectedAssignments = assignments.filter((assignment) => selectedIds.includes(assignment.id));
      let assignmentsToEdit = selectedAssignments;

      if (selectedAssignments.length !== selectedIds.length) {
        const params = new URLSearchParams();
        selectedIds.forEach((id) => params.append('ids', id));
        const response = await axios.get(`${API_URL}/assetassignments/by-ids`, { params });
        assignmentsToEdit = response.data || [];
      }

      if (assignmentsToEdit.length === 0) {
        showAlert('danger', 'No assignments were found for editing.');
        return;
      }

      const firstAssignment = assignmentsToEdit[0];

      setBatchEditData({
        selectedAssignments: assignmentsToEdit.map((assignment) => ({
          id: assignment.id,
          assetId: assignment.assetId,
          assetName: assignment.asset?.name || assignment.asset?.Name || 'Unknown Asset',
          assetType: getAssetTypeName(assignment),
          employeeName: assignment.employeeName || assignment.EmployeeName || assignment.employeeId || assignment.EmployeeId || 'Unknown',
          serialNumber: assignment.serialNumber || '',
          isActive: assignment.isActive,
          assignedDate: assignment.assignedDate || assignment.AssignedDate || null,
          deliveryMethod: assignment.deliveryMethod ?? assignment.DeliveryMethod ?? 0,
          deliveryStatus: assignment.deliveryStatus ?? assignment.DeliveryStatus ?? 0,
          courierService: assignment.courierService || assignment.CourierService || '',
          trackingId: assignment.trackingId || assignment.TrackingId || '',
          pickupPersonName: assignment.pickupPersonName || assignment.PickupPersonName || '',
          pickupPersonMobile: assignment.pickupPersonMobile || assignment.PickupPersonMobile || '',
          notes: assignment.notes || assignment.Notes || ''
        })),
        selectedProperties: [],
        propertyToAdd: '',
        assignedDate: formatDateForInputIST(firstAssignment.assignedDate || firstAssignment.AssignedDate) || getTodayInputDateIST(),
        deliveryMethod: firstAssignment.deliveryMethod ?? firstAssignment.DeliveryMethod ?? 0,
        deliveryStatus: firstAssignment.deliveryStatus ?? firstAssignment.DeliveryStatus ?? 0,
        courierService: firstAssignment.courierService || firstAssignment.CourierService || '',
        trackingId: firstAssignment.trackingId || firstAssignment.TrackingId || '',
        pickupPersonName: firstAssignment.pickupPersonName || firstAssignment.PickupPersonName || '',
        pickupPersonMobile: firstAssignment.pickupPersonMobile || firstAssignment.PickupPersonMobile || '',
        notes: firstAssignment.notes || firstAssignment.Notes || ''
      });
      setShowBatchEditModal(true);
    } catch (error) {
      console.error('Error loading selected assignments for editing:', error);
      showAlert('danger', 'Failed to load selected assignments');
    } finally {
      setSaving(false);
    }
  };



  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAssignment(null);
    setFormData(getInitialFormData());
  };

  const handleCloseBatchEditModal = () => {
    setShowBatchEditModal(false);
    setEditingAssignment(null);
    setBatchEditData(getInitialBatchEditData());
  };

  const handleBatchEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isBulkMode = batchEditData.selectedAssignments.length > 1;
      await Promise.all(batchEditData.selectedAssignments.map((assignment) => {
        const payload = {
          assignedDate: assignment.assignedDate || null,
          serialNumber: assignment.serialNumber?.trim() || null,
          deliveryMethod: assignment.deliveryMethod,
          deliveryStatus: assignment.deliveryStatus,
          courierService: assignment.courierService || null,
          trackingId: assignment.trackingId || null,
          pickupPersonName: assignment.pickupPersonName || null,
          pickupPersonMobile: assignment.pickupPersonMobile || null,
          notes: assignment.notes || null
        };

        if (!isBulkMode) {
          payload.assignedDate = batchEditData.assignedDate || null;
          payload.deliveryMethod = batchEditData.deliveryMethod;
          payload.deliveryStatus = batchEditData.deliveryStatus;
          payload.courierService = batchEditData.courierService || null;
          payload.trackingId = batchEditData.trackingId || null;
          payload.pickupPersonName = batchEditData.pickupPersonName || null;
          payload.pickupPersonMobile = batchEditData.pickupPersonMobile || null;
          payload.notes = batchEditData.notes || null;
        } else {
          const selectedProperties = new Set(batchEditData.selectedProperties || []);

          if (selectedProperties.has('assignedDate')) {
            payload.assignedDate = batchEditData.assignedDate || null;
          }

          if (selectedProperties.has('deliveryMethod')) {
            payload.deliveryMethod = batchEditData.deliveryMethod;
            if (batchEditData.deliveryMethod === 0) {
              payload.deliveryStatus = 2;
              payload.courierService = null;
              payload.trackingId = null;
              payload.pickupPersonName = null;
              payload.pickupPersonMobile = null;
            } else if (batchEditData.deliveryMethod === 1) {
              payload.pickupPersonName = null;
              payload.pickupPersonMobile = null;
            } else if (batchEditData.deliveryMethod === 2) {
              payload.courierService = null;
              payload.trackingId = null;
            }
          }

          if (selectedProperties.has('deliveryStatus')) {
            payload.deliveryStatus = batchEditData.deliveryStatus;
          }

          if (selectedProperties.has('notes')) {
            payload.notes = batchEditData.notes || null;
          }

          if (selectedProperties.has('courierService')) {
            payload.courierService = batchEditData.courierService || null;
          }

          if (selectedProperties.has('trackingId')) {
            payload.trackingId = batchEditData.trackingId || null;
          }

          if (selectedProperties.has('pickupPersonName')) {
            payload.pickupPersonName = batchEditData.pickupPersonName || null;
          }

          if (selectedProperties.has('pickupPersonMobile')) {
            payload.pickupPersonMobile = batchEditData.pickupPersonMobile || null;
          }

          if (selectedProperties.has('serialNumber')) {
            payload.serialNumber = assignment.serialNumber?.trim() || null;
          }
        }

        return axios.put(`${API_URL}/assetassignments/${assignment.id}`, payload);
      }));

      handleCloseBatchEditModal();
      showAlert('success', batchEditData.selectedAssignments.length === 1
        ? 'Assignment updated successfully!'
        : `${batchEditData.selectedAssignments.length} assignments updated successfully!`);
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error updating selected assignments:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to update selected assignments';
      showAlert('danger', `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async (assignmentId) => {
    try {
      await axios.put(`${API_URL}/assetassignments/${assignmentId}/return`);
      showAlert('success', 'Asset returned successfully!');
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error returning asset:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to return asset';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const openReturnDeadModal = (assignment) => {
    if (!assignment?.serialNumber?.trim()) {
      showAlert('danger', 'This assignment does not have a serial number, so it cannot be marked dead.');
      return;
    }

    setSelectedAssignmentForDeadReturn(assignment);
    setDeadReturnNotes('');
    setShowReturnDeadModal(true);
  };

  const closeReturnDeadModal = () => {
    setShowReturnDeadModal(false);
    setSelectedAssignmentForDeadReturn(null);
    setDeadReturnNotes('');
  };

  const getDeadActionLabel = (assignment) => (assignment?.isActive ? 'Return and Mark Dead' : 'Mark Dead');
  const getDeadActionModalTitle = (assignment) => (assignment?.isActive ? 'Return and Mark Dead' : 'Mark as Dead');

  const handleReturnAndMarkDead = async () => {
    if (!selectedAssignmentForDeadReturn) return;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/assetassignments/${selectedAssignmentForDeadReturn.id}/return`);
      await axios.post(`${API_URL}/assets/${selectedAssignmentForDeadReturn.assetId}/dead-items`, {
        items: [
          {
            saralAssetId: selectedAssignmentForDeadReturn.saralAssetId || null,
            serialNumber: selectedAssignmentForDeadReturn.serialNumber,
            notes: deadReturnNotes.trim() || `Marked dead after return from ${selectedAssignmentForDeadReturn.employeeName || selectedAssignmentForDeadReturn.employeeId}`
          }
        ]
      });

      closeReturnDeadModal();
      showAlert('success', 'Asset returned and marked dead successfully!');
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error returning and marking asset dead:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to return and mark asset dead';
      showAlert('danger', `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assignmentId) => {
    if (window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      try {
        await axios.delete(`${API_URL}/assetassignments/${assignmentId}`);
        showAlert('success', 'Assignment deleted successfully!');
        if (groupByEmployee) {
          fetchAssignmentGroups();
        } else {
          fetchAssignments();
        }
        fetchAvailableAssets();
      } catch (error) {
        console.error('Error deleting assignment:', error);
        const errorMsg = error.response?.data || error.message || 'Failed to delete assignment';
        showAlert('danger', `Error: ${errorMsg}`);
      }
    }
  };

  const handleDeleteAll = async () => {
    try {
      const allIdsResponse = await axios.get(`${API_URL}/assetassignments/ids`, {
        params: getAssignmentQueryParams(1, tablePageSize, true)
      });

      await Promise.all((allIdsResponse.data || []).map(id => axios.delete(`${API_URL}/assetassignments/${id}`)));
      setShowDeleteAllConfirm(false);
      showAlert('success', 'All assignments deleted successfully!');
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error deleting all assignments:', error);
      showAlert('danger', 'Failed to delete all assignments');
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/assetassignments/${id}`)));
      setShowDeleteSelectedConfirm(false);
      setSelectedIds([]);
      showAlert('success', `${selectedIds.length} assignment(s) deleted successfully!`);
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
      fetchAvailableAssets();
    } catch (error) {
      console.error('Error deleting selected assignments:', error);
      showAlert('danger', 'Failed to delete selected assignments');
    }
  };

  const toggleSelectAll = () => {
    const currentPageIds = assignments.map(a => a.id);
    const areAllCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));

    if (areAllCurrentPageSelected && selectedIds.length === totalAssignments) {
      setSelectedIds([]);
    } else {
      axios.get(`${API_URL}/assetassignments/ids`, {
        params: getAssignmentQueryParams(1, tablePageSize, true)
      }).then((response) => {
        setSelectedIds(response.data || []);
      }).catch((error) => {
        console.error('Error loading assignment ids:', error);
        showAlert('danger', 'Failed to select all filtered assignments');
      });
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleClubbedAssignmentReceipt = async () => {
    let selectedAssignments = assignments.filter((assignment) => selectedIds.includes(assignment.id));
    if (selectedAssignments.length !== selectedIds.length) {
      const params = new URLSearchParams();
      selectedIds.forEach((id) => params.append('ids', id));
      const response = await axios.get(`${API_URL}/assetassignments/by-ids`, {
        params
      });
      selectedAssignments = response.data || [];
    }

    if (selectedAssignments.length < 2) {
      showAlert('danger', 'Select at least two assignments to generate a clubbed receipt.');
      return;
    }

    const employeeIds = [...new Set(selectedAssignments.map((assignment) => String(assignment.employeeId || assignment.EmployeeId || '')))];
    if (employeeIds.length !== 1) {
      showAlert('danger', 'Clubbed assignment receipts can only be generated for assets assigned to the same employee.');
      return;
    }

    const hasTransfer = selectedAssignments.some((assignment) => (assignment.deliveryMethod ?? assignment.DeliveryMethod) === 3);
    if (hasTransfer) {
      showAlert('danger', 'Transfers cannot be mixed into a clubbed assignment receipt. Please generate transfer receipts separately.');
      return;
    }

    try {
      await ReceiptService.generateAssignmentReceipt(selectedAssignments.map((assignment) => assignment.id));
    } catch (error) {
      console.error('Error generating clubbed assignment receipt:', error);
      showAlert('danger', 'Failed to generate clubbed assignment receipt.');
    }
  };

  const handleViewTimeline = async (assignment) => {
    try {
      const response = await axios.get(`${API_URL}/assets/${assignment.assetId}/timeline`, {
        params: {
          saralAssetId: assignment.saralAssetId || undefined,
          serialNumber: assignment.serialNumber || undefined
        }
      });
      setAssetTimeline(response.data);
      setTimelineAssetId(assignment.assetId);
      setShowTimelineModal(true);
    } catch (error) {
      console.error('Error fetching asset timeline:', error);
    }
  };

  const handleTransfer = (assignment) => {
    setTransferData({
      fromEmployeeId: assignment.employeeId,
      assignmentId: assignment.id,
      toEmployeeId: '',
      notes: ''
    });
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      console.log('Transfer data before validation:', transferData); // Debug log
      
      if (!transferData.toEmployeeId || !transferData.assignmentId) {
        console.error('Missing required fields:', {
          toEmployeeId: transferData.toEmployeeId,
          assignmentId: transferData.assignmentId
        });
        return;
      }
      
      const payload = {
        assignmentId: parseInt(transferData.assignmentId),
        toEmployeeId: transferData.toEmployeeId, // Keep as string, don't parse
        notes: transferData.notes
      };
      
      console.log('Transfer payload:', payload); // Debug log
      
      const response = await axios.post(`${API_URL}/assetassignments/transfer`, payload);
      const currentAssignment = assignments.find((item) => String(item.id) === String(transferData.assignmentId));

      const createdAssignmentId = response?.data?.id ?? response?.data?.Id;
      if (createdAssignmentId) {
        await ReceiptService.generateTransferReceipt(createdAssignmentId);
      }

      setShowTransferModal(false);
      setTransferData({ fromEmployeeId: '', assignmentId: '', toEmployeeId: '', notes: '' });
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error transferring asset:', error);
      console.error('Error response:', error.response?.data); // Debug error details
    } finally {
      setSaving(false);
    }
  };

  const getRowClass = (item) => {
    if (item.isGroup) return 'table-light fw-bold';
    if (!item.isActive) return 'table-secondary';
    const statusClasses = {
      0: 'table-warning',
      1: 'table-info', 
      2: 'table-success'
    };
    return statusClasses[item.deliveryStatus] || '';
  };

  function getAssetTypeName(assignment) {
    const asset = assignment?.asset || assignment?.Asset;
    const assetType = asset?.assetType ?? asset?.AssetType;
    if (typeof assetType === 'string') return assetType;
    return assetType?.name || assetType?.Name || '';
  }

  const assetTypeOptions = [
    { value: '', label: 'All Asset Types' },
    ...Array.from(new Set([
      ...assignments.map(getAssetTypeName),
      ...assets.map(a => a.assetType?.name || a.assetTypeName || a.AssetType?.Name || a.AssetType || '')
    ].filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(typeName => ({ value: typeName, label: typeName }))
  ];

  const employeeOptions = [
    { value: '', label: 'All Employees' },
    ...employees.map(emp => ({
      value: emp.id ?? emp.Id,
      label: emp.userName || emp.UserName || emp.name || emp.Name || (emp.id ?? emp.Id)
    }))
  ];

  const deliveryStatusOptions = [
    { value: '', label: 'All Delivery Statuses' },
    { value: '0', label: 'Pending' },
    { value: '1', label: 'In Transit' },
    { value: '2', label: 'Delivered' }
  ];

  const assignmentStatusOptions = [
    { value: '', label: 'All Assignment Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'returned', label: 'Returned' }
  ];

  const groupedEmployeeIds = assignmentGroups.map(group => group.employeeId);
  const areAllGroupedExpanded = groupedEmployeeIds.length > 0 && groupedEmployeeIds.every(id => expandedEmployees.has(id));
  const hasActiveQuickFilters = Boolean(
    filters.assetType ||
    filters.employee ||
    filters.deliveryStatus !== '' ||
    filters.status !== '' ||
    filters.dateRange.start ||
    filters.dateRange.end
  );

  const quickFilterToolbar = (
    <div className="d-flex flex-wrap flex-md-nowrap align-items-center gap-2 h-100 w-100 grid-filter-toolbar-dense">
      <SearchableSelect
        className="flex-grow-1 grid-filter-select grid-filter-select-dense"
        controlClassName="form-control-sm"
        options={assetTypeOptions}
        value={filters.assetType}
        placeholder="All Asset Types"
        onChange={(e) => {
          setFilters({ ...filters, assetType: e.target.value });
          setTablePage(1);
          setGroupedCurrentPage(1);
        }}
      />
      <SearchableSelect
        className="flex-grow-1 grid-filter-select grid-filter-select-wide grid-filter-select-dense"
        controlClassName="form-control-sm"
        options={employeeOptions}
        value={filters.employee}
        placeholder="All Employees"
        onChange={(e) => {
          setFilters({ ...filters, employee: e.target.value });
          setTablePage(1);
          setGroupedCurrentPage(1);
        }}
      />
      <SearchableSelect
        className="flex-grow-1 grid-filter-select grid-filter-select-dense"
        controlClassName="form-control-sm"
        options={deliveryStatusOptions}
        value={filters.deliveryStatus}
        placeholder="All Delivery Statuses"
        onChange={(e) => {
          setFilters({ ...filters, deliveryStatus: e.target.value });
          setTablePage(1);
          setGroupedCurrentPage(1);
        }}
      />
      <SearchableSelect
        className="flex-grow-1 grid-filter-select grid-filter-select-dense"
        controlClassName="form-control-sm"
        options={assignmentStatusOptions}
        value={filters.status}
        placeholder="All Assignment Statuses"
        onChange={(e) => {
          setFilters({ ...filters, status: e.target.value });
          setTablePage(1);
          setGroupedCurrentPage(1);
        }}
      />
      <button
        className="btn btn-sm btn-outline-secondary grid-filter-clear"
        onClick={() => {
          setFilters(emptyFilters);
          setTablePage(1);
          setGroupedCurrentPage(1);
        }}
        disabled={!hasActiveQuickFilters}
      >
        <i className="fas fa-eraser me-1"></i>Clear
      </button>
    </div>
  );

  const columns = [
    { 
      field: 'checkbox', 
      label: (
        <input 
          type="checkbox" 
          checked={assignments.length > 0 && assignments.every(item => selectedIds.includes(item.id))}
          onChange={toggleSelectAll}
        />
      ),
      render: (value, item) => (
        <input 
          type="checkbox" 
          checked={selectedIds.includes(item.id)}
          onChange={() => toggleSelect(item.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    { field: 'saralAssetId', label: 'Saral Asset ID', sortField: 'saralAssetId' },
    { 
      field: 'asset', 
      label: 'Asset',
      sortField: 'assetName',
      render: (value) => value?.name || value?.Name || 'N/A',
      sortValue: (value, row) => (value?.name || value?.Name || row?.asset?.name || row?.asset?.Name || '').toString().toLowerCase()
    },
    { field: 'serialNumber', label: 'Serial Number', sortField: 'serialNumber' },
    {
      field: 'employeeName',
      label: 'Employee',
      sortField: 'employeeName',
      render: (value, row) => value || row?.EmployeeName || row?.employeeId || row?.EmployeeId || 'Unknown',
      sortValue: (value, row) => (value || row?.EmployeeName || row?.employeeId || row?.EmployeeId || '').toString().toLowerCase()
    },
    { 
      field: 'deliveryMethod', 
      label: 'Delivery Method',
      sortField: 'deliveryMethod',
      render: (value) => ['Self Pickup', 'Courier', 'Other Person', 'Transfer'][value] || value,
      sortValue: (value) => Number(value ?? -1)
    },
    { 
      field: 'deliveryStatus', 
      label: 'Delivery Status',
      sortField: 'deliveryStatus',
      render: (value) => {
        const statusMap = {
          0: { text: 'Pending', class: 'bg-warning text-dark' },
          1: { text: 'In Transit', class: 'bg-info text-white' },
          2: { text: 'Delivered', class: 'bg-success text-white' }
        };
        const status = statusMap[value] || { text: value, class: 'bg-secondary' };
        return <span className={`badge ${status.class}`}>{status.text}</span>;
      },
      sortValue: (value) => Number(value ?? -1)
    },
    { 
      field: 'assignedDate', 
      label: 'Assigned Date',
      sortField: 'assignedDate',
      render: (value) => formatDisplayDateIST(value),
      sortValue: (value) => new Date(value).getTime()
    },
    { 
      field: 'returnDate', 
      label: 'Return Date',
      sortField: 'returnDate',
      render: (value) => value ? formatDisplayDateIST(value) : '-',
      sortValue: (value) => value ? new Date(value).getTime() : 0
    },
    { 
      field: 'isActive', 
      label: 'Status',
      sortField: 'isActive',
      render: (value) => (
        <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
          {value ? 'Active' : 'Returned'}
        </span>
      ),
      sortValue: (value) => value ? 1 : 0
    }
  ];

  const handleMarkDelivered = async (assignmentId) => {
    try {
      await axios.put(`${API_URL}/assetassignments/${assignmentId}/deliver`);
      if (groupByEmployee) {
        fetchAssignmentGroups();
      } else {
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error marking as delivered:', error);
    }
  };

  const closeDropdownMenu = (event) => {
    if (event?.currentTarget) {
      const dropdown = event.currentTarget.closest('.dropdown');
      dropdown?.querySelector('.dropdown-menu.show')?.classList.remove('show');
      const toggle = dropdown?.querySelector('[data-bs-toggle="dropdown"]');
      toggle?.setAttribute('aria-expanded', 'false');
    }
  };

  const customActions = (item, closeMenu = () => {}) => (
    <>
      <li>
        <button className="dropdown-item" onClick={() => { closeMenu(); printAssignmentReceiptForRow(item); }}>
          <i className="fas fa-file-invoice me-2"></i>Generate Receipt
        </button>
      </li>
      <li>
        <button className="dropdown-item" onClick={() => { closeMenu(); setSelectedAssignment(item); setShowQRModal(true); }}>
          <i className="fas fa-qrcode me-2"></i>QR Code
        </button>
      </li>
      <li>
        <button className="dropdown-item" onClick={() => { closeMenu(); handleViewTimeline(item); }}>
          <i className="fas fa-history me-2"></i>Timeline
        </button>
      </li>
      <li>
        {selectedIds.length > 1 ? (
          <button className="dropdown-item" onClick={() => { closeMenu(); handleEditSelected(); }}>
            <i className="fas fa-edit me-2"></i>Edit Selected ({selectedIds.length})
          </button>
        ) : (
          <button className="dropdown-item" onClick={() => { closeMenu(); handleEdit(item); }}>
            <i className="fas fa-edit me-2"></i>Edit Details
          </button>
        )}
      </li>
      {item.isActive && (
        <>
          {item.deliveryStatus === 1 && (
            <li>
              <button className="dropdown-item" onClick={() => { closeMenu(); handleMarkDelivered(item.id); }}>
                <i className="fas fa-check me-2 text-success"></i>Mark Delivered
              </button>
            </li>
          )}
          {item.deliveryStatus !== 0 && (
            <>
              <li>
                <button className="dropdown-item" onClick={() => { closeMenu(); handleTransfer(item); }}>
                  <i className="fas fa-exchange-alt me-2 text-info"></i>Transfer
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={() => { closeMenu(); handleReturn(item.id); }}>
                  <i className="fas fa-undo me-2 text-warning"></i>Return
                </button>
              </li>
            </>
          )}
        </>
      )}
      {item.serialNumber?.trim() && (
        <li>
          <button className="dropdown-item text-danger" onClick={() => { closeMenu(); openReturnDeadModal(item); }}>
            <i className="fas fa-ban me-2"></i>{getDeadActionLabel(item)}
          </button>
        </li>
      )}
    </>
  );

  const toggleAllEmployees = () => {
    const allEmployeeIds = assignmentGroups.map(group => group.employeeId);
    
    if (expandedEmployees.size === allEmployeeIds.length) {
      setExpandedEmployees(new Set());
    } else {
      setExpandedEmployees(new Set(allEmployeeIds));
    }
  };

  const toggleEmployee = (employeeId) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedEmployees(newExpanded);
  };

  const selectedAssignmentsInView = assignments.filter((assignment) => selectedIds.includes(assignment.id));
  const hasResolvedSelectedAssignments = selectedIds.length > 0 && selectedAssignmentsInView.length === selectedIds.length;
  const selectedEmployeeIds = [...new Set(selectedAssignmentsInView.map((assignment) => String(assignment.employeeId || assignment.EmployeeId || '')))];
  const selectedHasTransfer = selectedAssignmentsInView.some((assignment) => (assignment.deliveryMethod ?? assignment.DeliveryMethod) === 3);
  const canBulkEdit = !groupByEmployee && selectedIds.length > 1;
  const canClubReceipt = !groupByEmployee
    && selectedIds.length > 1
    && hasResolvedSelectedAssignments
    && selectedEmployeeIds.length === 1
    && !selectedHasTransfer;
  const canDeleteSelected = !groupByEmployee && selectedIds.length > 0;
  const hasMultipleRowsSelected = !groupByEmployee && selectedIds.length > 1;
  const assignmentSummaryTiles = groupByEmployee ? [
    { label: 'Total Assignments', value: assignmentSummary.total, tone: 'primary' },
    { label: 'Active Assignments', value: assignmentSummary.active, tone: 'success' },
    { label: 'Delivered', value: assignmentSummary.delivered, tone: 'info' },
    { label: 'Transfers', value: assignmentSummary.transfers, tone: 'warning' },
    { label: 'Employee Groups', value: totalEmployeeGroups, tone: 'dark' }
  ] : [
    { label: 'Total Assignments', value: assignmentSummary.total, tone: 'primary' },
    { label: 'Active Assignments', value: assignmentSummary.active, tone: 'success' },
    { label: 'Returned', value: assignmentSummary.returned, tone: 'secondary' },
    { label: 'Delivered', value: assignmentSummary.delivered, tone: 'info' },
    { label: 'In Transit', value: assignmentSummary.inTransit, tone: 'warning' },
    { label: 'Transfers', value: assignmentSummary.transfers, tone: 'danger' }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      <div className="page-header">
        <h1 className="page-title mb-0">Asset Assignments</h1>
        <div className="page-header-actions">
          {!groupByEmployee && selectedIds.length > 0 && (
            <>
              {canBulkEdit && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleEditSelected}
                >
                  <i className="fas fa-edit me-1"></i>Edit Selected ({selectedIds.length})
                </button>
              )}
              {canClubReceipt && (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={handleClubbedAssignmentReceipt}
                >
                  <i className="fas fa-file-signature me-1"></i>Club Receipt ({selectedIds.length})
                </button>
              )}
              {canDeleteSelected && (
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => setShowDeleteSelectedConfirm(true)}
                >
                  <i className="fas fa-trash-alt me-1"></i>Delete Selected ({selectedIds.length})
                </button>
              )}
            </>
          )}
          {!hasMultipleRowsSelected && (
            <button 
              className={`btn btn-sm ${groupByEmployee ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                const nextGroupBy = !groupByEmployee;
                setGroupByEmployee(nextGroupBy);
                if (nextGroupBy) {
                  setExpandedEmployees(new Set());
                  setGroupedCurrentPage(1);
                }
                setSelectedIds([]);
              }}
            >
              <i className={`fas fa-${groupByEmployee ? 'users' : 'list'} me-1`}></i>
              {groupByEmployee ? 'Grouped' : 'List'}
            </button>
          )}
          {!hasMultipleRowsSelected && (
            <button className="btn btn-sm btn-outline-info" onClick={() => setShowBulkModal(true)}>
              <i className="fas fa-upload me-1"></i><span className="d-none d-sm-inline">Bulk Operations</span><span className="d-sm-none">Bulk</span>
            </button>
          )}
          {!hasMultipleRowsSelected && (
            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowTransferModal(true)}>
              <i className="fas fa-exchange-alt me-1"></i><span className="d-none d-sm-inline">Transfer Asset</span><span className="d-sm-none">Transfer</span>
            </button>
          )}
          {!hasMultipleRowsSelected && (
            <button className="btn btn-sm btn-primary" onClick={handleAdd}>
              <i className="fas fa-plus me-1"></i><span className="d-none d-sm-inline">Assign Assets</span><span className="d-sm-none">Assign</span>
            </button>
          )}
        </div>
      </div>

      <div className="row g-3 mb-4">
        {assignmentSummaryTiles.map((tile) => (
          <div key={tile.label} className="col-6 col-md-4 col-xl-2">
            <div className={`card border-0 shadow-sm h-100 border-start border-4 border-${tile.tone} ${summaryLoading ? 'dashboard-skeleton-card' : ''}`}>
              <div className="card-body">
                {summaryLoading ? (
                  <>
                    <div className="dashboard-skeleton-line dashboard-skeleton-line-label mb-3" aria-hidden="true"></div>
                    <div className="dashboard-skeleton-line dashboard-skeleton-line-value" aria-hidden="true"></div>
                    <span className="visually-hidden">Loading {tile.label}</span>
                  </>
                ) : (
                  <>
                    <div className="text-muted small text-uppercase mb-2">{tile.label}</div>
                    <div className="fs-4 fw-semibold">{tile.value}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {groupByEmployee ? (
            loading ? (
              <div className="table-loader p-4">
                <Loader text="Loading assignments..." size="medium" />
              </div>
            ) : (
            <div className="p-3">
              <div className="mb-3">
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleAllEmployees}
                >
                  <i className={`fas fa-${areAllGroupedExpanded ? 'minus' : 'plus'}-square me-1`}></i>
                  {areAllGroupedExpanded ? 'Collapse All' : 'Expand All'}
                </button>
              </div>
              {(() => {
                const totalPages = Math.max(1, Math.ceil(totalEmployeeGroups / groupedItemsPerPage));
                const currentPage = Math.min(groupedCurrentPage, totalPages);
                if (assignmentGroups.length === 0) return <div className="text-center py-5"><p>No assignments</p></div>;
                return (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">
                        Showing {assignmentGroups.length} of {totalEmployeeGroups} employees
                      </small>
                      <small className="text-muted">Page {currentPage} / {totalPages}</small>
                    </div>
                    {assignmentGroups.map((group) => {
                const empId = group.employeeId;
                const empAssignments = group.assignments || [];
                const empName = group.employeeName || empId;
                const isDark = document.body.getAttribute('data-theme') === 'dark';
                return (
                  <div key={empId} className="card mb-3 shadow-sm" style={{border: '1px solid #dee2e6', overflow: 'visible'}}>
                    <div 
                      className="card-header border-bottom d-flex justify-content-between align-items-center" 
                      style={{
                        cursor: 'pointer', 
                        borderRadius: expandedEmployees.has(empId) ? '0.375rem 0.375rem 0 0' : '0.375rem',
                        backgroundColor: isDark ? '#374151' : '#f8f9fa',
                        color: isDark ? '#e2e8f0' : '#212529'
                      }}
                      onClick={() => toggleEmployee(empId)}
                    >
                      <div>
                        <i className={`fas fa-chevron-${expandedEmployees.has(empId) ? 'down' : 'right'} me-2`}></i>
                        <i className="fas fa-user me-2"></i>
                        <strong>{empName}</strong>
                      </div>
                      <span className="badge bg-primary text-white">{group.count || empAssignments.length} assets</span>
                    </div>
                    {expandedEmployees.has(empId) && (
                      <div className="card-body p-0" style={{overflow: 'visible'}}>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Saral Asset ID</th>
                                <th>Asset</th>
                                <th>Serial Number</th>
                                <th>Delivery Method</th>
                                <th>Status</th>
                                <th>Assigned Date</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {empAssignments.map((assignment) => (
                                <tr key={assignment.id}>
                                  <td>{assignment.saralAssetId}</td>
                                  <td>{assignment.asset?.name || 'N/A'}</td>
                                  <td>{assignment.serialNumber}</td>
                                  <td>{['Self Pickup', 'Courier', 'Other Person', 'Transfer'][assignment.deliveryMethod]}</td>
                                  <td>
                                    <span className={`badge ${assignment.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                      {assignment.isActive ? 'Active' : 'Returned'}
                                    </span>
                                  </td>
                                  <td>{formatDisplayDateIST(assignment.assignedDate)}</td>
                                  <td>
                                    <div className="dropdown">
                                      <button className="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown">
                                        <i className="fas fa-ellipsis-v"></i>
                                      </button>
                                      <ul className="dropdown-menu dropdown-menu-end">
                                        <li>
                                          <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); printAssignmentReceiptForRow(assignment); }}>
                                            <i className="fas fa-file-invoice me-2"></i>Generate Receipt
                                          </button>
                                        </li>
                                        <li>
                                          <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); setSelectedAssignment(assignment); setShowQRModal(true); }}>
                                            <i className="fas fa-qrcode me-2"></i>QR Code
                                          </button>
                                        </li>
                                        <li>
                                          <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleViewTimeline(assignment); }}>
                                            <i className="fas fa-history me-2"></i>Timeline
                                          </button>
                                        </li>
                                        <li>
                                          {selectedIds.length > 1 ? (
                                            <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleEditSelected(); }}>
                                              <i className="fas fa-edit me-2"></i>Edit Selected ({selectedIds.length})
                                            </button>
                                          ) : (
                                            <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleEdit(assignment); }}>
                                              <i className="fas fa-edit me-2"></i>Edit Details
                                            </button>
                                          )}
                                        </li>
                                        {assignment.isActive && assignment.deliveryStatus === 1 && (
                                          <li>
                                            <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleMarkDelivered(assignment.id); }}>
                                              <i className="fas fa-check me-2 text-success"></i>Mark Delivered
                                            </button>
                                          </li>
                                        )}
                                        {assignment.isActive && assignment.deliveryStatus !== 0 && (
                                          <>
                                            <li>
                                              <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleTransfer(assignment); }}>
                                                <i className="fas fa-exchange-alt me-2 text-info"></i>Transfer
                                              </button>
                                            </li>
                                            <li>
                                              <button className="dropdown-item" onClick={(e) => { closeDropdownMenu(e); handleReturn(assignment.id); }}>
                                                <i className="fas fa-undo me-2 text-warning"></i>Return
                                              </button>
                                            </li>
                                          </>
                                        )}
                                        {assignment.serialNumber?.trim() && (
                                          <li>
                                            <button className="dropdown-item text-danger" onClick={(e) => { closeDropdownMenu(e); openReturnDeadModal(assignment); }}>
                                              <i className="fas fa-ban me-2"></i>{getDeadActionLabel(assignment)}
                                            </button>
                                          </li>
                                        )}
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                          <button className="dropdown-item text-danger" onClick={(e) => { closeDropdownMenu(e); handleDelete(assignment.id); }}>
                                            <i className="fas fa-trash me-2"></i>Delete
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
                    })}
                    {totalPages > 1 && (
                      <nav>
                        <ul className="pagination justify-content-center mt-2">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const maxVisible = 5;
                            const halfVisible = Math.floor(maxVisible / 2);
                            let startPage = Math.max(1, currentPage - halfVisible);
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                            
                            if (endPage - startPage < maxVisible - 1) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }
                            
                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => setGroupedCurrentPage(i)}>{i}</button>
                                </li>
                              );
                            }
                            return pages;
                          })()}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </>
                );
              })()}
            </div>
            )
          ) : (
            <DataTable
              data={assignments}
              columns={columns}
              customActions={customActions}
              onDelete={handleDelete}
              getRowClass={getRowClass}
              toolbarContent={quickFilterToolbar}
              toolbarInset
              serverSide
              loading={loading}
              page={tablePage}
              pageSize={tablePageSize}
              totalItems={totalAssignments}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={(field, direction) => {
                setSortField(field);
                setSortDirection(direction);
                setTablePage(1);
              }}
              onPageChange={setTablePage}
              getExportData={() => fetchAllPaginatedItems(async (page, pageSize) => {
                const response = await axios.get(`${API_URL}/assetassignments/paged`, {
                  params: getAssignmentQueryParams(page, pageSize, true)
                });

                return response.data;
              })}
              exportFileName="asset-assignments"
            />
          )}
        </div>
      </div>

      <AssignmentEditorModal
        show={showModal}
        saving={saving}
        editingAssignment={editingAssignment}
        formData={formData}
        employees={employees}
        assets={assets}
        assetFilter={assetFilter}
        setAssetFilter={setAssetFilter}
        setFormData={setFormData}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />

      <AssignmentBatchEditModal
        show={showBatchEditModal}
        saving={saving}
        batchEditData={batchEditData}
        setBatchEditData={setBatchEditData}
        onClose={handleCloseBatchEditModal}
        onSubmit={handleBatchEditSubmit}
      />


      {/* Transfer Modal */}
      <div className={`modal fade ${showTransferModal ? 'show d-block' : ''}`} style={{backgroundColor: showTransferModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exchange-alt me-2"></i>
                Transfer Asset
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowTransferModal(false)}></button>
            </div>
            <form onSubmit={handleTransferSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">From Employee & Asset</label>
                  <SearchableSelect
                    options={assignments.filter(a => a.isActive).map(assignment => ({
                      value: assignment.id,
                      label: `${assignment.employeeName || assignment.EmployeeName || assignment.employeeId || assignment.EmployeeId || 'Unknown'} - ${assignment.asset?.name || assignment.asset?.Name || 'Unknown Asset'} (${assignment.saralAssetId || 'N/A'})`
                    }))}
                    value={transferData.assignmentId}
                    onChange={(e) => setTransferData({ ...transferData, assignmentId: e.target.value })}
                    placeholder="Select current assignment"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">To Employee</label>
                  <SearchableSelect
                    options={employees.map(employee => ({ 
                      value: employee.id ?? employee.Id,
                      label: employee.userName || employee.UserName || employee.name || employee.Name || (employee.id ?? employee.Id)
                    }))}
                    value={transferData.toEmployeeId}
                    onChange={(e) => {
                      console.log('Selected employee ID:', e.target.value); // Debug log
                      setTransferData({ ...transferData, toEmployeeId: e.target.value });
                    }}
                    placeholder="Select new employee"
                    required
                  />
                  <small className="text-muted">Current value: {transferData.toEmployeeId || 'empty'}</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Transfer Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter transfer reason/notes"
                    value={transferData.notes}
                    onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTransferModal(false)}>
                  Cancel
                </button>
                <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving || !transferData.assignmentId || !transferData.toEmployeeId}>
                  {saving ? (
                    <Loader size="small" />
                  ) : (
                    <>
                      <i className="fas fa-exchange-alt me-1"></i>
                      Transfer Asset
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      <div className={`modal fade ${showQRModal ? 'show d-block' : ''}`} style={{backgroundColor: showQRModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-qrcode me-2"></i>
                Assignment QR Code
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedAssignment && (
                <QRCodeGenerator 
                  asset={{
                    id: selectedAssignment.id,
                    name: `${selectedAssignment.asset?.name || selectedAssignment.asset?.Name || 'Unknown Asset'} - ${selectedAssignment.employeeName || selectedAssignment.EmployeeName || selectedAssignment.employeeId || selectedAssignment.EmployeeId || 'Unknown'}`,
                    saralAssetId: selectedAssignment.saralAssetId,
                    serialNumber: selectedAssignment.serialNumber,
                    assetType: selectedAssignment.asset?.assetType
                  }} 
                  onClose={() => setShowQRModal(false)} 
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Modal */}
      <div className={`modal fade ${showTimelineModal ? 'show d-block' : ''}`} style={{backgroundColor: showTimelineModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg timeline-modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-history me-2"></i>
                Asset Timeline
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowTimelineModal(false)}></button>
            </div>
            <div className="modal-body timeline-modal-body">
              {assetTimeline.length > 0 ? (
                <div className="timeline">
                  {assetTimeline.map((event, index) => (
                    <div key={index} className="timeline-item mb-3">
                      <div className="d-flex">
                        <div className="timeline-marker me-3">
                          <div className={`rounded-circle p-2 ${event.type === 'assignment' ? 'bg-success' : event.type === 'return' ? 'bg-warning' : 'bg-info'}`}>
                            <i className={`fas ${event.type === 'assignment' ? 'fa-user-plus' : event.type === 'return' ? 'fa-undo' : 'fa-exchange-alt'} text-white`}></i>
                          </div>
                        </div>
                        <div className="timeline-content flex-grow-1">
                          <div className="card border-0 shadow-sm">
                            <div className="card-body">
                              <h6 className="card-title mb-1">{event.action}</h6>
                              <p className="card-text text-muted mb-2">{event.description}</p>
                              <small className="text-muted">
                                <i className="fas fa-clock me-1"></i>
                                {formatDisplayDateTimeIST(event.date)}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-history fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No timeline data available for this asset.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Operations Modal */}
      <AssignmentBulkOperations 
        show={showBulkModal} 
        onHide={() => setShowBulkModal(false)}
        onRefresh={() => {
          if (groupByEmployee) {
            fetchAssignmentGroups();
          } else {
            fetchAssignments();
          }
          fetchAvailableAssets();
        }}
      />

      <div className={`modal fade ${showReturnDeadModal ? 'show d-block' : ''}`} style={{backgroundColor: showReturnDeadModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-ban me-2 text-danger"></i>
                {getDeadActionModalTitle(selectedAssignmentForDeadReturn)}
              </h5>
              <button type="button" className="btn-close" onClick={closeReturnDeadModal}></button>
            </div>
            <div className="modal-body">
              {selectedAssignmentForDeadReturn && (
                <>
                  <div className="alert alert-light border">
                    <div><strong>Asset:</strong> {selectedAssignmentForDeadReturn.asset?.name || 'Unknown Asset'}</div>
                    <div><strong>Saral Asset ID:</strong> {selectedAssignmentForDeadReturn.saralAssetId || '-'}</div>
                    <div><strong>Serial Number:</strong> {selectedAssignmentForDeadReturn.serialNumber}</div>
                    <div><strong>Employee:</strong> {selectedAssignmentForDeadReturn.employeeName || selectedAssignmentForDeadReturn.employeeId}</div>
                  </div>
                  <label className="form-label">Dead Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Optional reason or return condition"
                    value={deadReturnNotes}
                    onChange={(e) => setDeadReturnNotes(e.target.value)}
                  ></textarea>
                  <small className="text-muted d-block mt-2">
                    {selectedAssignmentForDeadReturn?.isActive
                      ? 'This will first return the assignment, then move this unit into dead stock.'
                      : 'This will move this returned unit into dead stock.'}
                  </small>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeReturnDeadModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleReturnAndMarkDead} disabled={saving || !selectedAssignmentForDeadReturn}>
                {saving ? (
                  <>
                    <Loader size="small" />
                    <span className="ms-2">Processing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-ban me-1"></i>{getDeadActionLabel(selectedAssignmentForDeadReturn)}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeleteAllConfirm ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteAllConfirm ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title"><i className="fas fa-exclamation-triangle me-2"></i>Confirm Delete All</h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteAllConfirm(false)}></button>
            </div>
            <div className="modal-body">
              <p className="text-danger fw-bold">⚠️ WARNING: This will delete ALL {totalAssignments} assignments permanently!</p>
              <p>This action cannot be undone. Are you absolutely sure?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteAllConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteAll}>
                <i className="fas fa-trash-alt me-1"></i>Yes, Delete All
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeleteSelectedConfirm ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteSelectedConfirm ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title"><i className="fas fa-exclamation-triangle me-2"></i>Confirm Delete Selected</h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteSelectedConfirm(false)}></button>
            </div>
            <div className="modal-body">
              <p className="text-danger fw-bold">⚠️ WARNING: This will delete {selectedIds.length} selected assignment(s) permanently!</p>
              <p>This action cannot be undone. Are you sure?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteSelectedConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteSelected}>
                <i className="fas fa-trash-alt me-1"></i>Yes, Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetAssignments;
